import { useEffect, useState } from "react";
import { dummyItems } from "@/layout-components/WidgetContainer/dummyItems";
import { PricingModal } from "@/ui-components/PricingModal";
import { trackPaywallView, trackTrialCtaClick } from "@/utils/gtm";

interface WidgetNoAccessProps {
  widgetName: string;
}

export function WidgetNoAccess({ widgetName }: WidgetNoAccessProps) {
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  // This component only renders for someone already signed in, so the
  // population is a plan limit rather than the demo wall.
  useEffect(() => {
    trackPaywallView({
      surface: "widget_no_access",
      widget_name: widgetName,
      authenticated: true,
    });
  }, [widgetName]);

  return (
    <>
      <PricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />
      <div className="relative h-full flex flex-col">
        {/* Blurred background content */}
        <div className="flex-1 overflow-auto filter scrollbar-none blur-sm pointer-events-none bg-gray-900">
          {dummyItems.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between px-4 py-2 border-b border-gray-700 text-xs text-gray-300"
            >
              <span className="whitespace-nowrap mr-2">{item.time}</span>
              <span className="flex-1 truncate">{item.title}</span>
              <span className="ml-2">{"↓"}</span>
            </div>
          ))}
        </div>

        {/* Dim backdrop */}
        <div className="absolute inset-0 bg-black/20 pointer-events-none" />

        {/* No-access CTA */}
        <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
          <div className="bg-gray-900/95 backdrop-blur-sm rounded-md p-4 sm:p-5 text-center max-w-[240px] w-full mx-auto border border-gray-700 pointer-events-auto">
            {/* Lock icon with terminal-orange accent */}
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-terminal-orange"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>

            {/* Heading */}
            <h2 className="text-sm text-gray-100 font-medium mb-1">
              Upgrade Required
            </h2>

            {/* Subtitle */}
            <p className="text-xs text-gray-500 mb-4">
              Upgrade your plan to access {widgetName || "this widget"}
            </p>

            {/* CTA button */}
            <button
              onClick={() => {
                trackTrialCtaClick({
                  surface: "widget_no_access",
                  widget_name: widgetName,
                  authenticated: true,
                });
                setIsPricingOpen(true);
              }}
              className="w-full px-4 py-1.5 text-xs font-medium text-white bg-squawk-blue hover:bg-squawk-blue/80 rounded-md transition-colors cursor-pointer"
            >
              Upgrade Plan
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
